// src/pages/admin/orders/AdminOrderEditPage.tsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FullScreenLoader } from "components/ui/Loader";
import { formatMoney } from "utils/money";
import { getErrorMessage, isVersionConflict, conflictCurrentVersion } from "utils/errors";
import { adminGetOrder, patchStatus, type AdminPatchStatusIn, type AdminOrderOut } from "api/adminOrders";

import StatusUpdateForm from "features/admin/orders/StatusUpdateForm";
import ResultsUploadPanel from "features/admin/orders/ResultsUploadPanel";

type LineGeom = {
  left: number;
  titleTop: number;
  titleBottom: number;
  containerTop: number;
  containerBottom: number;
};

const GOLD = "#f3dfae";

const AdminOrderEditPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const orderId = Number(params.id);

  const [order, setOrder] = useState<AdminOrderOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Title/container vertical line geometry (CookiesPage logic)
  const pageRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<LineGeom | null>(null);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await adminGetOrder(orderId);
      setOrder(res);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to load order"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Number.isFinite(orderId)) void load();
    else {
      setErr("Invalid order id");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useLayoutEffect(() => {
    const page = pageRef.current;
    const title = titleRef.current;
    const container = containerRef.current;
    if (!page || !title || !container) return;

    const compute = () => {
      const p = page.getBoundingClientRect();
      const t = title.getBoundingClientRect();
      const c = container.getBoundingClientRect();

      const left = Math.round(t.left + t.width / 2 - p.left);

      setGeom({
        left: Math.max(0, left),
        titleTop: Math.max(0, Math.round(t.top - p.top)),
        titleBottom: Math.max(0, Math.round(t.bottom - p.top)),
        containerTop: Math.max(0, Math.round(c.top - p.top)),
        containerBottom: Math.max(0, Math.round(c.bottom - p.top))
      });
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(page);
    ro.observe(title);
    ro.observe(container);

    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [loading, err, order?.id, order?.lock_version]);

  const handleStatusSubmit = async (payload: AdminPatchStatusIn) => {
    if (!order) return;
    setErr("");
    try {
      const updated = await patchStatus(order.id, payload);
      setOrder(updated);
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current
            ? `Version conflict (current v${current}). Reloaded latest.`
            : "Version conflict. Reloaded latest."
        );
        await load();
      } else {
        setErr(getErrorMessage(e, "Failed to update order"));
      }
      throw e;
    }
  };

  if (loading) return <FullScreenLoader label="Loading…" />;

  const TITLE_GAP_PX = 14;
  const CONTAINER_TOP_MARGIN_CLASS = "mt-16 sm:mt-20";

  if (err) {
    return (
      <main className="min-h-screen px-4 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          <Link to="/admin/orders" className="text-blue-600 hover:underline">
            ← Back to Orders
          </Link>
          <div className="border border-red-300 bg-red-50 text-red-700 p-3 rounded">{err}</div>
        </div>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="min-h-screen px-4 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          <Link to="/admin/orders" className="text-blue-600 hover:underline">
            ← Back to Orders
          </Link>
          <div className="text-gray-600">Order not found.</div>
        </div>
      </main>
    );
  }

  const created = order.created_date ? new Date(order.created_date).toLocaleString() : "—";

  const totalCents =
    typeof order.total_cents === "number"
      ? order.total_cents
      : (order.subtotal_cents ?? 0) +
        (order.surcharge_cents ?? 0) +
        (order.tax_cents ?? 0) +
        (order.fees_cents ?? 0);

  const status = (order.status || "—").toString().toLowerCase();
  const statusPillClass =
    status === "paid" || status === "fulfilled" || status === "complete"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "cancelled" || status === "refunded"
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-amber-50 text-amber-800 border-amber-200";

  const userLabel = order.user_label ?? order.user_display_name ?? order.user_email ?? "—";

  return (
    <main ref={pageRef} className="relative min-h-screen px-4 py-12 sm:py-16">
      {/* Vertical gold line segments (with gaps for title + container) */}
      {geom && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: 0,
              bottom: `calc(100% - ${Math.max(0, geom.titleTop - TITLE_GAP_PX)}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.titleBottom + TITLE_GAP_PX,
              bottom: `calc(100% - ${geom.containerTop}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.containerBottom,
              bottom: 0
            }}
          />
        </>
      )}

      <div className="relative z-10">
        {/* Title */}
        <header className="text-center">
          <h1
            ref={titleRef}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]"
          >
            Admin · Order #{order.id}
          </h1>
        </header>

        {/* Full-width container */}
        <section className={[CONTAINER_TOP_MARGIN_CLASS, "flex justify-center"].join(" ")}>
          <div
            ref={containerRef}
            className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8"
          >
            {/* Top row */}
            <div className="flex items-start justify-between gap-4">
              <Link
                to="/admin/orders"
                className="font-ui text-xs uppercase tracking-wide text-slate-500 hover:opacity-80"
              >
                ← Back to Orders
              </Link>

              <div className="flex items-center gap-3">
                <span
                  className={[
                    "inline-flex items-center rounded-full px-3 py-1",
                    "font-ui text-xs uppercase tracking-wide border",
                    statusPillClass
                  ].join(" ")}
                >
                  {order.status}
                </span>
                <span className="font-ui text-xs uppercase tracking-wide text-slate-500">
                  v{order.lock_version}
                </span>
              </div>
            </div>

            {/* Summary card */}
            <section className="mt-6 rounded-2xl border p-5" style={{ borderColor: `${GOLD}B3` }}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">Status</div>
                  <div className="mt-1 font-display text-lg font-semibold text-slate-900">
                    {order.status}
                  </div>
                </div>

                <div>
                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">Created</div>
                  <div className="mt-1 font-body text-slate-700">{created}</div>
                </div>

                <div>
                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">Total</div>
                  <div className="mt-1 font-display text-lg font-semibold text-slate-900">
                    {formatMoney(totalCents ?? 0, order.currency)}
                  </div>
                </div>

                <div>
                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">Package</div>
                  <div className="mt-1 font-display text-lg font-semibold text-slate-900">
                    {order.package_name ?? "—"}
                  </div>
                </div>

                <div>
                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">Clinic</div>
                  <div className="mt-1 font-display text-lg font-semibold text-slate-900">
                    {order.clinic_name ?? "—"}
                  </div>
                </div>

                <div>
                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">User</div>
                  <div className="mt-1 font-display text-lg font-semibold text-slate-900 break-words">
                    {userLabel}
                  </div>
                </div>
              </div>
            </section>

            {/* Full-width vertical sections (no side-by-side) */}
            <div className="mt-8 space-y-8">
              <section className="rounded-2xl border border-slate-200 p-5">
                <h2 className="font-ui font-bold uppercase tracking-wide text-slate-800">
                  Update status
                </h2>
                <div className="mt-4">
                  <StatusUpdateForm order={order} onSubmit={handleStatusSubmit} />
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 p-5">
                <h2 className="font-ui font-bold uppercase tracking-wide text-slate-800">
                  Upload results
                </h2>
                <div className="mt-4">
                  <ResultsUploadPanel
                    orderId={order.id}
                    orderLockVersion={order.lock_version}
                    onOrderChanged={load}
                  />
                </div>
              </section>
            </div>

            {/* Bottom note */}
            <div className="mt-8 border-t border-slate-200 pt-4">
              <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                Changes are versioned. If you see a conflict, refresh and try again.
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default AdminOrderEditPage;
